import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    createPalette,
    PaletteButton,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import {
    useChangeRequestActionsCommands,
    useChangeRequestCopyCommands,
    useChangeRequestNavigationCommands,
} from '../../../Components';

/**
 * Button to display secondary actions relative to the change request.
 */
export function ChangeRequestSettingsButton(
    props: {
        space: api.Space;
        changeRequest: api.ChangeRequest;
    } & Omit<ButtonProps, 'onPress' | 'label'>
): React.ReactElement {
    const { space, changeRequest, ...buttonProps } = props;

    const copyCommands = useChangeRequestCopyCommands({ space, changeRequest });
    const actionsPaletteGroup = useChangeRequestActionsCommands({
        spaceKey: space.id,
        changeRequest,
    });
    const navigationPaletteGroup = useChangeRequestNavigationCommands({ space, changeRequest });

    const utilityCommands = [copyCommands];
    const navigationCommands = Array.isArray(navigationPaletteGroup.commands)
        ? navigationPaletteGroup.commands
        : [];

    return (
        <PaletteButton
            palette={() =>
                createPalette({
                    position: {
                        width: PalettePositionWidth.MEDIUM,
                        maxHeight: PalettePositionMaxHeight.AUTO,
                        preferredPlacement: 'bottom',
                    },
                    groups: [
                        {
                            key: 'changeRequest.navigation',
                            commands: navigationCommands,
                        },
                        {
                            key: 'changeRequest.utility',
                            commands: utilityCommands,
                        },
                        actionsPaletteGroup,
                    ].filter(filterOutNullable),
                })
            }
            leadingIcon={Icon.MoreVertical}
            label="Change request options"
            kind="blank"
            size="medium"
            iconOnly
            {...buttonProps}
        />
    );
}
